import { Amplify } from 'aws-amplify';
import { signOut as cognitoSignOut } from 'aws-amplify/auth';
import { signIn } from 'aws-amplify/auth';
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import type { AuthProvider } from '@aws-amplify/auth/dist/esm/types/inputs';
import { getAppStage } from 'src/utils/app';
import { getSsoHost } from 'src/utils/ssoReferrer';

let isAmplifyInitialized = false;

/**
 * Where Cognito sends the V1 Amplify federated flow back to.
 *
 * This used to be `${origin}/sign_in`. Because Amplify derives it from whatever
 * origin the page happens to be on, EVERY app host needed its own registered
 * Cognito callback URL -- ~48 of a hard quota of 100, for atlas / beacon / ad /
 * admanager / omni / discover / digital-shelf / shopperanalytics / shopperos /
 * brandclubos across prod, dev, qa, beta, gamma and localhost.
 *
 * None of them can actually be reached any more: federated sign-in only starts
 * from SignInV2Container, which AuthShell renders only on the SSO host (an
 * app-domain `/sign_in` is bounced to the SSO host before any form is shown).
 * So `origin` is always the SSO host already, and deriving it bought nothing
 * while costing the quota.
 *
 * Pinning it to the SSO host for the current stage mirrors what the V2 PKCE
 * path has always done (`getRedirectUri` in cognitoAuth.ts) and reduces V1's
 * requirement to the two URLs that path already uses.
 */
export const getAmplifyRedirectUri = (): string => `https://${getSsoHost()}/sign_in`;

export const initializeAmplify = () => {
  const stage = getAppStage();
  const graphqlEndpoint =
    stage === 'prod'
      ? 'https://2jcdzattqbg7hb4s2no5dukny4.appsync-api.us-west-2.amazonaws.com/graphql'
      : 'https://kv6s3v5xhzeevaaambfhx6jw3e.appsync-api.us-west-2.amazonaws.com/graphql';
  const eventsEndpoint =
    stage === 'prod'
      ? 'https://z4ygoftixvbbbpyd2dudgwqrga.appsync-api.us-west-2.amazonaws.com/event'
      : 'https://uovpjgiplzbafogcs5gdztpbwm.appsync-api.us-west-2.amazonaws.com/event';

  try {
    if (!isAmplifyInitialized) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: 'us-west-2_op1uPQUYe',
            userPoolClientId: '5ktrvofvdidmkmoku14jmkff81',
            signUpVerificationMethod: 'code',
            identityPoolId: 'us-west-2:2da8aef8-aaa8-4277-8c0d-738fe1d897b8',
            loginWith: {
              oauth: {
                domain: 'stackline-enterprise-sso-signin.auth.us-west-2.amazoncognito.com',
                scopes: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
                redirectSignIn: [getAmplifyRedirectUri()],
                redirectSignOut: [getAmplifyRedirectUri()],
                responseType: 'code'
              }
            }
          }
        },
        API: {
          GraphQL: {
            endpoint: graphqlEndpoint,
            region: 'us-west-2',
            defaultAuthMode: 'lambda'
          },
          Events: {
            endpoint: eventsEndpoint,
            region: 'us-west-2',
            defaultAuthMode: 'lambda'
          }
        }
      });
      isAmplifyInitialized = true;
    }
  } catch (e) {
    console.warn(e);
  }
};

export const amplifySignOut = async () => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
  }
  try {
    await cognitoSignOut();
    return true;
  } catch (e) {
    console.error('Error with sign out', e);
    return false;
  }
};

/**
 * Safely fetches the current auth session with proper initialization and error handling
 * @returns The auth session if successful, null if there was an error
 */
export const safelyFetchAuthSession = async () => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
    console.log('Amplify config:', Amplify.getConfig());
  }

  try {
    const session = await fetchAuthSession();
    return session;
  } catch (error) {
    console.error('Failed to fetch auth session:', error);
    return null;
  }
};

export const amplifySignIn = async (email: string, password: string) => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
  }
  try {
    const { isSignedIn } = await signIn({ username: email, password });
    return isSignedIn;
  } catch (e) {
    console.error('Error signing in', e);
    return false;
  }
};

export const amplifyFederatedSignIn = async (provider: AuthProvider | { custom: string }, customState: string) => {
  if (!isAmplifyInitialized) {
    initializeAmplify();
    console.log('Amplify config:', Amplify.getConfig());
  }
  try {
    const authProvider = typeof provider === 'string' ? { custom: provider } : provider;

    const stateWithProvider = {
      ...JSON.parse(customState),
      identity_provider: authProvider.custom
    };

    await signInWithRedirect({
      provider: authProvider,
      customState: JSON.stringify(stateWithProvider)
    });
    return true;
  } catch (e: any) {
    // Stale Amplify session — sign out and retry once.
    if (e?.name === 'UserAlreadyAuthenticatedException') {
      try {
        await cognitoSignOut();
        const authProvider = typeof provider === 'string' ? { custom: provider } : provider;
        const stateWithProvider = { ...JSON.parse(customState), identity_provider: authProvider.custom };
        await signInWithRedirect({ provider: authProvider, customState: JSON.stringify(stateWithProvider) });
        return true;
      } catch (retryErr) {
        console.error('Error with federated sign in (retry)', retryErr);
        return false;
      }
    }
    console.error('Error with federated sign in', e);
    return false;
  }
};
